import {
    Card,
    CardContent,
    CardHeader, FormControl,
    Grid,
    IconButton, Input, InputAdornment,
    Paper, Table,
    TableBody,
    TableCell,
    TableContainer, TableFooter,
    TableHead, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip
} from "@mui/material";
import theme2 from "../../../lib/theme";
import * as React from "react";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Loading from "../../../components/loading";
import TableHeaderRow from "../../../components/TableHeaderRow";
import StripedTableRow from "../../../components/StripedTableRow";
import HttpsIcon from "@mui/icons-material/Https";
import TableFooterRow from "../../../components/TableFooterRow";
import moment from "moment";

const defaultSummary = {clicks: 0, rpc: 0, rev: 0};
export default function Brokers(params: {dateRange: any, reload: any}) {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([] as any[]);
    const [search, setSearch] = React.useState('');
    const [filtered, setFiltered] = React.useState([] as any[]);
    const [summary, setSummary] = React.useState({...defaultSummary} as any);
    const [sort, setSort] = React.useState({column: 'clicks', sortAsc: false});

    const reload = async () => {
        setLoading(true);

        // do a post to https://membertool.banneredgemedia.com/integration/publishers

        const s = moment.tz(moment(params.dateRange[0]).format("YYYY-MM-DD"), "America/Los_Angeles").format();
        let e = moment(params.dateRange[1]).format();
        let dash = e.lastIndexOf("-");
        e = e.substring(0, dash);
        e = moment.tz(e, "America/Los_Angeles").format();

        const response = await fetch('https://auto.ratesforinsurance.com/integration/brokers', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                startDate: s,
                endDate: e,
                dimensions: ['publisher', 'provider', 'brand'],
            })
        });

        const json = await response.json();
        setData(json);
        calcFiltered(json, search, sort);

        setLoading(false);
    }


    React.useEffect(() => {
        if (params.reload) {
            reload().then(() => {});
        }
    }, [params.reload]);

    const calcFiltered = (_data: any[], _search: string, _sort: any) => {
        let dat = _data.filter((d: any) => !_search.trim()
            || d.publisher.toLowerCase().includes(_search.trim().toLowerCase())
            || d.brand.toLowerCase().includes(_search.trim().toLowerCase())
            || d.provider.toLowerCase().includes(_search.trim().toLowerCase())
        );

        dat = dat.sort((a: any, b: any) => {
            if (_sort.sortAsc === false) {
                if (a[_sort.column] < b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            } else {
                if (a[_sort.column] > b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            }
        });

        setFiltered(dat);

        let sum = {...defaultSummary};
        dat.forEach((d: any) => {
            sum.clicks += d.clicks;
            sum.rev += d.rev;
        });
        if (sum.clicks > 0) {
            sum.rpc = sum.rev / sum.clicks;
        }
        setSummary(sum);
    }

    const doSort = (column: string, defaultAsc: boolean = false) => {
        let s;
        if (sort.column === column) {
            s = {column: column, sortAsc: !sort.sortAsc};
        } else {
            s = {column: column, sortAsc: defaultAsc};
        }
        setSort(s);

        calcFiltered(data, search, s);
    }

    return (
        <Card sx={{height: '563px', maxWidth: 'calc(100vw - 10px)'}}>
            <CardHeader title={"Brokers"} sx={{backgroundColor: theme2.palette.primary.main, color: theme2.palette.primary.contrastText, padding: '5px'}}
                        titleTypographyProps={{sx:{fontSize: '18px'}}}
                        action={
                <>
                    <IconButton aria-label="refresh" sx={{color: theme2.palette.primary.contrastText}} onClick={reload}>
                        <CachedIcon />
                    </IconButton>
                </>
                        }
            />
            <CardContent>
                {loading ? <Box sx={{marginTop: '130px'}}><Loading/></Box> : <>
                    <FormControl variant="outlined" sx={{ m: 1, maxWidth: 190 }} size="small">
                        <Input
                            id="search"
                            type="text"
                            spellCheck="false"
                            placeholder="Search"
                            value={search || ''}
                            onChange={(e: any) => {setSearch(e.target.value); calcFiltered(data, e.target.value, sort)}}
                            startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                        />
                    </FormControl>

                    <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 40px)', overflowY: 'auto', maxHeight: '433px'}}>
                        <Table size="small"
                               sx={{
                                   "& .MuiTableRow-root:hover": {
                                       backgroundColor: "primary.light"
                                   }
                               }}
                               stickyHeader={true}
                        >
                            <TableHead>
                                <TableHeaderRow>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'publisher'}
                                            direction={sort.column === 'publisher' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('publisher', true)}}
                                        >
                                            Publisher
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'provider'}
                                            direction={sort.column === 'provider' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('provider', true)}}
                                        >
                                            Provider
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'brand'}
                                            direction={sort.column === 'brand' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('brand', true)}}
                                        >
                                            Brand
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'clicks'}
                                            direction={sort.column === 'clicks' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('clicks', false)}}
                                        >
                                            Clicks
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'rpc'}
                                            direction={sort.column === 'rpc' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('rpc', false)}}
                                        >
                                            Avg. Bid
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'rev'}
                                            direction={sort.column === 'rev' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('rev', false)}}
                                        >
                                            Rev
                                        </TableSortLabel>
                                    </TableCell>

                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((row: any, index  ) => (
                                    <StripedTableRow key={index}>
                                        <TableCell sx={{minWidth: '200px'}}>{row.publisher}</TableCell>
                                        <TableCell sx={{minWidth: '200px'}}>{row.provider}</TableCell>
                                        <TableCell sx={{minWidth: '200px'}}>{row.brand}</TableCell>
                                        <TableCell align="right">{row.clicks.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                        <TableCell align="right">${row.rpc.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                        <TableCell align="right">${row.rev.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                    </StripedTableRow>
                                ))}
                                </TableBody>
                            <TableFooter>
                                <TableFooterRow>
                                    <TableCell>Totals</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">{summary.clicks.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">${summary.rpc.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                    <TableCell align="right">${summary.rev.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                </TableFooterRow>
                            </TableFooter>
                        </Table>

                    </TableContainer>
                </>}
            </CardContent>
        </Card>
    );
}

function getProfitColor(profit: number) {
    if (profit < -0) {
        return theme2.palette.error.light
    }
    return undefined;
}
